import { IconButton } from '@mui/material';
import { MdClose } from '@teamworksdev/icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationModal } from '../../../components/modals/delete-confirmation-modal';
import { useToast } from '../../../hooks/useToast';
import { WorkoutWithCycleAndSession } from '../types';
import { getDisplayNameForWorkout } from '../utils';
import {
  SingleWorkoutEditContext,
  SingleWorkoutSheet,
} from './SingleWorkoutSheet';

type PendingAction = 'remove' | 'cancel' | null;

export function WorkoutEditPanel({
  workout,
  editContext,
  onClose,
  mode = 'edit',
  overlayOpen = false,
}: {
  workout: WorkoutWithCycleAndSession;
  editContext: SingleWorkoutEditContext;
  onClose: () => void;
  mode?: 'edit' | 'add';
  overlayOpen?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const titleId = workout.workoutId;
  const sectionRef = useRef<HTMLElement>(null);
  const [modalOrDrawerOpen, setModalOrDrawerOpen] = useState(false);

  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [pendingWorkoutId, setPendingWorkoutId] = useState<string>('');

  // Move focus into the panel when it opens so keyboard / screen-reader users
  // land on the new content immediately.
  useEffect(() => {
    sectionRef.current?.focus({ preventScroll: true });
  }, []);

  // Close on Escape from anywhere, regardless of where focus currently sits.
  // A `document`-level listener is required because the trigger (a calendar
  // card) lives outside this subtree, so a React `onKeyDown` here would never
  // fire until the user focused something inside the panel.
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      // While a drawer or modal opened from the sheet is open, let it handle
      // Escape rather than closing the panel.
      if (modalOrDrawerOpen || overlayOpen) {
        return;
      }
      // While a cell is being edited it renders a focused <input>/<textarea>
      // inside the panel; in that case defer to the Sheet so Escape cancels
      // the edit instead of closing the panel.
      const active = document.activeElement;
      const isEditingCell =
        active instanceof HTMLElement &&
        !!sectionRef.current?.contains(active) &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable);
      if (isEditingCell) {
        return;
      }
      if (pendingAction !== null) {
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [onClose, modalOrDrawerOpen, overlayOpen, pendingAction]);

  const sheetEditContext: SingleWorkoutEditContext = {
    ...editContext,
    miscMutationFns: {
      ...editContext.miscMutationFns,
      onDeleteWorkout: (workoutId: string) => {
        setPendingWorkoutId(workoutId);
        setPendingAction('remove');
      },
      onCancelWorkout: (workoutId: string) => {
        setPendingWorkoutId(workoutId);
        setPendingAction('cancel');
      },
    },
  };

  const handleConfirm = () => {
    if (pendingAction === 'remove' && editContext.onDeleteWorkout) {
      editContext.onDeleteWorkout(pendingWorkoutId);
      onClose();
      toast({ title: t('success'), description: t('workoutDeleted'), severity: 'success' });
    } else if (pendingAction === 'cancel' && editContext.onCancelWorkout) {
      editContext.onCancelWorkout(pendingWorkoutId);
      onClose();
    }
    setPendingAction(null);
  };

  const modalTitle =
    pendingAction === 'cancel' ? t('cancelWorkoutAction') : t('deleteWorkout');
  const modalMessage =
    pendingAction === 'cancel'
      ? t('cancelWorkoutMessage')
      : t('deleteWorkoutMessage');
  const modalConfirmText =
    pendingAction === 'cancel' ? t('yes') : undefined;

  return (
    <>
      <section
        ref={sectionRef}
        role="region"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="flex h-full w-full flex-col overflow-hidden bg-white outline-none"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-3">
          <div className="min-w-0 flex flex-col">
            <h2
              id={titleId}
              className="m-0 truncate text-base font-semibold text-gray-900"
            >
              {mode === 'add' ? t('addWorkout') : t('editWorkoutTitle')}
            </h2>
            {mode !== 'add' && (
              <span className="truncate text-xs text-gray-500">
                {workout.name ?? getDisplayNameForWorkout(workout)}
              </span>
            )}
          </div>
          <IconButton
            aria-label={t('close')}
            onClick={onClose}
            size="small"
            sx={{ padding: 0, width: '30px', height: '30px' }}
          >
            <MdClose size={20} />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-2">
          <SingleWorkoutSheet
            workout={workout}
            editContext={sheetEditContext}
            onModalOrDrawerOpenChange={setModalOrDrawerOpen}
          />
        </div>
      </section>
      <DeleteConfirmationModal
        id={pendingWorkoutId}
        open={pendingAction !== null}
        title={modalTitle}
        message={modalMessage}
        confirmText={modalConfirmText}
        onDelete={handleConfirm}
        onClose={() => setPendingAction(null)}
      />
    </>
  );
}
